import React from 'react'
import PropTypes from 'prop-types'
import {
  Box,
  Grid,
  Paper,
  Skeleton,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material'
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined'
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import { CommonButton } from '@/components/common'
import { formatIsoDateWithWeekday, formatUtcTimeRange } from '@/utils'

const IconBadge = ({ icon: Icon, size = 40 }) => {
  const theme = useTheme()
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '10px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: alpha(theme.palette.primary.main, 0.1),
        color: 'primary.main',
        flexShrink: 0,
      }}
    >
      <Icon sx={{ fontSize: size * 0.5 }} />
    </Box>
  )
}

const InfoField = ({ icon: Icon, label, children }) => (
  <Stack spacing={0.5} sx={{ minWidth: 0 }}>
    <Stack direction='row' spacing={0.75} alignItems='center'>
      <Icon sx={{ fontSize: 16, color: 'text.secondary' }} />
      <Typography variant='body2' color='text.secondary' fontWeight={600}>
        {label}
      </Typography>
    </Stack>
    <Typography variant='body2' fontWeight={500}>
      {children}
    </Typography>
  </Stack>
)

export const DriveResultsHeader = ({
  drive,
  loading,
  onDownloadSummary,
  loadingDownloadSummary,
}) => {
  if (loading) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 2,
          mb: 3,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Skeleton variant='text' width='40%' height={32} />
        <Skeleton variant='text' width='60%' sx={{ mt: 2 }} />
      </Paper>
    )
  }

  const hours = formatUtcTimeRange(drive?.start_time, drive?.end_time)
  const cityStateZip = [
    [drive?.city, drive?.state].filter(Boolean).join(', '),
    drive?.zip_code,
  ]
    .filter(Boolean)
    .join(' ')
  const cityStateZipWithSite = drive?.site_type
    ? `${cityStateZip} (${drive.site_type})`
    : cityStateZip

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, md: 3 },
        borderRadius: 2,
        mb: 3,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent='space-between'
        alignItems={{ xs: 'flex-start', md: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Stack direction='row' spacing={1.5} alignItems='center'>
          <IconBadge icon={CalendarMonthOutlinedIcon} />
          <Typography variant='h5' fontWeight={700}>
            {drive?.name}
          </Typography>
        </Stack>
        <CommonButton
          variant='secondary'
          size='small'
          startIcon={<DownloadOutlinedIcon />}
          loading={loadingDownloadSummary}
          onClick={onDownloadSummary}
        >
          Download Summary
        </CommonButton>
      </Stack>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <InfoField icon={CalendarMonthOutlinedIcon} label='Date'>
            {drive?.date ? formatIsoDateWithWeekday(drive.date) : '—'}
          </InfoField>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <InfoField icon={AccessTimeOutlinedIcon} label='Time'>
            {hours || '—'}
          </InfoField>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <InfoField icon={LocationOnOutlinedIcon} label='Location'>
            {drive?.address || '—'}
            {cityStateZipWithSite && (
              <>
                <br />
                {cityStateZipWithSite}
              </>
            )}
          </InfoField>
        </Grid>
      </Grid>
    </Paper>
  )
}

DriveResultsHeader.propTypes = {
  drive: PropTypes.object,
  loading: PropTypes.bool,
  onDownloadSummary: PropTypes.func,
  loadingDownloadSummary: PropTypes.bool,
}
